import { isApiError } from '@/lib/api';

/** Staff wording for the API's codes (RL-988); the API message shows under it. */
export const LAB_PACKAGE_ERRORS: Record<string, string> = {
  VERSION_CONFLICT:
    'Someone else changed this package. It has been reloaded: check it and save again.',
  LAB_PACKAGE_NOT_FOUND: 'This package no longer exists.',
  LAB_PACKAGE_EXISTS: 'A package with this name already exists.',
  LAB_PACKAGE_UNKNOWN_TESTS:
    'Every test must be an active test of the lab catalogue.',
  MEMBER_PRICE_ABOVE_PRICE: 'The member price cannot be above the price.',
  UNKNOWN_BRANCH: 'One of the chosen branches is unknown.',
  LAB_BOOKING_SETTINGS_INVALID: 'These home-visit settings are not valid.',
  LAB_BOOKING_NOT_FOUND:
    'This booking is no longer at your branch. The list has been reloaded.',
  LAB_BOOKING_NOT_CHANGEABLE:
    'This booking can no longer be cancelled: it was already cancelled or the sample was taken.',
  LAB_BOOKING_CHANGED:
    'The booking changed since you opened it. It has been reloaded: check it and try again.',
  APPOINTMENT_STATUS_CONFLICT:
    'The branch visit was changed at the front desk meanwhile. The booking has been reloaded: check it and try again.',
  LAB_BOOKING_PAID:
    'A payment was recorded on this booking’s bill. Refund and void it in Billing first, then cancel.',
  LAB_BOOKING_BILL_CLOSED:
    'This booking’s bill is finalised (for example sent to a panel). Void it in Billing first, then cancel.',
};

/** Codes after which the row on screen is out of date. */
export const RELOAD_CODES = new Set([
  'VERSION_CONFLICT',
  'LAB_PACKAGE_NOT_FOUND',
  'LAB_BOOKING_NOT_FOUND',
  'LAB_BOOKING_NOT_CHANGEABLE',
  'LAB_BOOKING_CHANGED',
  'APPOINTMENT_STATUS_CONFLICT',
]);

export interface Failure {
  title: string;
  detail?: string;
  requestId?: string;
  /** LAB_PACKAGE_UNKNOWN_TESTS: the tests the catalogue does not know. */
  unknownTests?: string[];
}

export function toFailure(err: unknown): Failure {
  if (!isApiError(err))
    return { title: 'Could not reach the server. Try again.' };
  const known = LAB_PACKAGE_ERRORS[err.code];
  const p: Record<string, unknown> = err.problem ?? {};
  return {
    title: known ?? err.message,
    detail: known && err.message !== known ? err.message : undefined,
    requestId: err.requestId,
    unknownTests: Array.isArray(p.unknownTests)
      ? (p.unknownTests as string[])
      : undefined,
  };
}

/** Minutes after midnight → "08:00". */
export const minToTime = (m: number) =>
  `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;

/** "08:30" → 510; null when not a time. */
export function timeToMin(s: string): number | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(s.trim());
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h > 23 || min > 59) return null;
  return h * 60 + min;
}

/** A whole number within [min, max], or null. */
export function intIn(s: string, min: number, max: number): number | null {
  if (!/^\d+$/.test(s.trim())) return null;
  const n = Number(s.trim());
  return n >= min && n <= max ? n : null;
}
